import {
  AloftDate,
  AloftPrice,
  AloftTime,
  alertRequiresNavAttention,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import { useDesktopReconciliationComparison } from '../../modules/reconciliation/hooks/useDesktopReconciliationComparison.ts';
import { useAlerts } from '../../modules/referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { selectAlerts } from '../../redux/features/selectors/commentarySelectors/selectAlerts.ts';
import {
  selectCommentary,
  selectCommentaryV2,
} from '../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectOpinionOfSiteValuePrice } from '../../redux/features/selectors/opinionOfSiteValueSelectors.ts';
import {
  selectCompSequence,
  selectIncludeCostApproach,
  selectIsMockOrder,
  selectOrder,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectImprovementsEntities } from '../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationById } from '../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectHasPartialOrFinishedBasementComp } from '../../redux/features/selectors/selectHasPartialOrFinishedBasementComp.ts';
import { selectReconciliation } from '../../redux/features/selectors/selectReconciliation.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { hasBasementAreaDisclosureNote } from '../../utils/basementAreaDisclosure.ts';
import { schAppraisifyValidation } from './schemas.ts';
import type { OrganizedValidationErrors } from './types.ts';
import { ValidationErrorType, ValidationKey } from './types.ts';

export const useAppraisifyValidation = () => {
  const subjectPropertyIdentification = useAppSelector((state) =>
    selectPropertyIdentificationById(state, 'subject'),
  );
  const order = useAppSelector(selectOrder);
  const compSequence = useAppSelector(selectCompSequence);
  const improvements = useAppSelector(selectImprovementsEntities);
  const opinionOfSiteValue = useAppSelector(selectOpinionOfSiteValuePrice);
  const { marketConditionsV2, priorSales } = useAppSelector(selectCommentaryV2);
  const {
    totalCostToCure,
    opinionOfMarketValue,
    opinionOfAfterRepairValue,
    totalRenovationBudget,
    includeTotalCostToCure,
    includeTotalRenovationBudget,
    includeOpinionOfAfterRepairValue,
    valueTypes,
    arvBelowAivOverride,
  } = useAppSelector(selectReconciliation);
  const hasPartialOrFinishedBasementComp = useAppSelector(selectHasPartialOrFinishedBasementComp);
  const { reconciliation, alerts: alertsCommentary } = useAppSelector(selectCommentary);
  const dismissedAlerts = useAppSelector(selectAlerts).dismissedAlerts ?? [];
  const finalizedExplanations = useAppSelector((state) => state.ui.finalizedExplanations);
  const explanationContext = { dismissedAlerts, finalizedExplanations };
  const { hasAivDiscrepancy, hasArvDiscrepancy } = useDesktopReconciliationComparison({
    opinionOfMarketValue: schAloftPrice.optional().parse(opinionOfMarketValue),
    opinionOfAfterRepairValue: schAloftPrice.optional().parse(opinionOfAfterRepairValue),
  });

  const alerts = useAlerts();
  const allAlerts = Object.values(alerts).flat();

  const isArvOrder = order.hasArvOption;
  const isMockOrder = useAppSelector(selectIsMockOrder);
  const shouldRequireReconciliationFields = !isMockOrder;
  const includeCostApproach = useAppSelector(selectIncludeCostApproach);
  const estimatedROI =
    isArvOrder && opinionOfAfterRepairValue && opinionOfMarketValue && totalRenovationBudget
      ? schAloftPrice
          .parse(opinionOfAfterRepairValue)
          .sub(schAloftPrice.parse(opinionOfMarketValue))
          .sub(schAloftPrice.parse(totalRenovationBudget))
      : null;

  const shouldShowFeasibilityCommentary =
    isArvOrder && estimatedROI !== null && estimatedROI.toDollarLike() <= 0;
  const shouldShowRevisionTab = order.isRevision || !!order.revisionData?.revisionType;

  const validationForm = schAppraisifyValidation.safeParse({
    alerts: {
      alerts: allAlerts,
    },
    orderDetails: {
      zoning: subjectPropertyIdentification?.zoning ?? '',
      zoningCompliance: subjectPropertyIdentification?.zoningCompliance ?? '',
      parcelNumber: subjectPropertyIdentification?.parcelNumber ?? '',
      newConstruction: subjectPropertyIdentification?.newConstruction ?? false,
      legalDescription: subjectPropertyIdentification?.legalDescription ?? '',
      hoa: subjectPropertyIdentification?.hoa ?? '',
      effectiveDate: order.effectiveDate ?? '',
      intendedUsers: order.intendedUsers ?? '',
      intendedUse: order.intendedUse ?? '',
      totalRenovationBudget: totalRenovationBudget ?? '',
      locationType: subjectPropertyIdentification?.locationType ?? '',
      propertyInterest: subjectPropertyIdentification?.propertyInterest ?? '',
      propertyInterestOther: subjectPropertyIdentification?.propertyInterestOther ?? '',
    },
    market: {},
    analysis: {
      opinionOfSiteValue: opinionOfSiteValue ?? '',
    },
    commentary: {
      estimatedDemand: marketConditionsV2?.estimatedDemandSupply ?? '',
      estimatedExposureTime: marketConditionsV2?.estimatedExposureTime ?? '',
      marketConditionCommentary: marketConditionsV2?.commentary ?? '',
      priorSalesCommentary: priorSales?.commentary ?? '',
    },
    reconciliation: {
      totalCostToCure,
      totalCostToCureCommentary: reconciliation?.totalCostToCureCommentary ?? '',
      opinionOfMarketValue,
      opinionOfAfterRepairValue,
      totalRenovationBudget,
      includeTotalRenovationBudget: includeTotalRenovationBudget ?? false,
      includeOpinionOfAfterRepairValue: includeOpinionOfAfterRepairValue ?? false,
      includeTotalCostToCure: includeTotalCostToCure ?? false,
      hasArvOption: order.hasArvOption,
      reconciliationCommentary: reconciliation?.reconciliationCommentary ?? '',
      aivReconciliationCommentary:
        reconciliation?.aivReconciliationCommentary ??
        reconciliation?.reconciliationCommentary ??
        '',
      arvReconciliationCommentary: reconciliation?.arvReconciliationCommentary ?? '',
      highestAndBestUseCommentary: reconciliation?.highestAndBestUseCommentary ?? '',
      revisionCommentary: reconciliation?.revisionCommentary ?? '',
      alternativeComparablesCommentary: reconciliation?.alternativeComparablesCommentary ?? [],
      feasibilityCommentary: reconciliation?.feasibilityCommentary ?? '',
      aivDiscrepancyCommentary: reconciliation?.aivDiscrepancyCommentary ?? '',
      arvDiscrepancyCommentary: reconciliation?.arvDiscrepancyCommentary ?? '',
      hasAivDiscrepancy,
      hasArvDiscrepancy,
      isMockOrder,
      valueTypes: valueTypes ?? [],
      arvBelowAivOverride: arvBelowAivOverride ?? false,
      arvBelowAivOverrideCommentary: reconciliation?.arvBelowAivOverrideCommentary ?? '',
    },
  });

  const processValidationErrors = (): OrganizedValidationErrors => {
    const organizedErrors: OrganizedValidationErrors = {
      Alerts: {},
      OrderDetails: {},
      Analysis: {},
      Market: {},
      Commentary: {},
      Reconciliation: {},
      Global: {},
      Comparables: {},
      Adjustments: {},
    };

    if (!order?.isInspectifyOrder) {
      return organizedErrors;
    }

    const dismissedStatusAlerts = allAlerts.filter(({ status }) => status === 'dismissed');
    if (dismissedStatusAlerts.length > 0) {
      const hasDismissedAlertsWithoutCommentary = dismissedStatusAlerts.some((alert) =>
        alertRequiresNavAttention(alert, alertsCommentary, explanationContext),
      );

      if (hasDismissedAlertsWithoutCommentary) {
        organizedErrors.Alerts[ValidationKey.AlertsCommentary] = {
          type: ValidationErrorType.REQUIRED,
          message: 'Please provide sufficient commentary for all dismissed alerts.',
        };
      }
    }

    const subjectQuality = improvements?.subject?.craftsmanQuality;
    const hasMissingQuality =
      !subjectQuality ||
      (compSequence && compSequence.some((compId) => !improvements?.[compId]?.craftsmanQuality));

    if (includeCostApproach && hasMissingQuality) {
      organizedErrors.Analysis.costQuality = {
        type: ValidationErrorType.REQUIRED,
        message:
          'Please provide a craftsman quality for the subject and all comparables before continuing.',
      };
    }

    const hasBasementAreaDisclosure = hasBasementAreaDisclosureNote(
      order.hasArvOption
        ? (reconciliation?.aivReconciliationCommentary ?? reconciliation?.reconciliationCommentary)
        : reconciliation?.reconciliationCommentary,
    );
    if (hasPartialOrFinishedBasementComp && !hasBasementAreaDisclosure) {
      organizedErrors.Comparables[ValidationKey.BasementAreaDisclosure] = {
        type: ValidationErrorType.REQUIRED,
        message:
          'Please complete the Basement Area Disclosure before leaving the Comparables page.',
      };
    }

    if (order.effectiveDate) {
      if (order.inspectionDate) {
        if (
          AloftDate.schema
            .parse(order.effectiveDate)
            .compare(AloftDate.schema.parse(order.inspectionDate)) < 0
        ) {
          organizedErrors.OrderDetails[ValidationKey.EffectiveDate] = {
            type: ValidationErrorType.INVALID,
            message: 'Effective date cannot be earlier than inspection date.',
          };
        }
      } else if (
        AloftDate.schema
          .parse(order.effectiveDate)
          .compare(AloftTime.schema.parse(order.createdAt).toAloftDate()) < 0
      ) {
        organizedErrors.OrderDetails[ValidationKey.EffectiveDate] = {
          type: ValidationErrorType.INVALID,
          message: 'Effective date cannot be earlier than assigned date.',
        };
      }
    }

    if (validationForm.error) {
      validationForm.error.issues.forEach((issue) => {
        const { path, code } = issue;
        let { message } = issue;

        if (!shouldShowRevisionTab && path.at(-1) === 'revisionCommentary') {
          return;
        }

        if (
          path.at(-1) === 'alternativeComparablesCommentary' &&
          (!shouldShowRevisionTab || !order.revisionData?.comparables)
        ) {
          return;
        }

        if (
          !includeTotalCostToCure &&
          (path.at(-1) === 'totalCostToCure' || path.at(-1) === 'totalCostToCureCommentary')
        ) {
          return;
        }

        if (!shouldShowFeasibilityCommentary && path.at(-1) === 'feasibilityCommentary') {
          return;
        }

        if (!hasAivDiscrepancy && path.at(-1) === 'aivDiscrepancyCommentary') {
          return;
        }

        if (!hasArvDiscrepancy && path.at(-1) === 'arvDiscrepancyCommentary') {
          return;
        }

        if (path.at(-1) === 'aivReconciliationCommentary' && !order.hasArvOption) {
          return;
        }

        if (path.at(-1) === 'arvReconciliationCommentary' && !order.hasArvOption) {
          return;
        }

        if (path.at(-1) === 'reconciliationCommentary' && order.hasArvOption) {
          return;
        }

        let type: ValidationErrorType = ValidationErrorType.REQUIRED;

        if (
          opinionOfAfterRepairValue &&
          opinionOfMarketValue &&
          path.at(-1) === 'feasibilityCommentary'
        ) {
          const renovationBudget = totalRenovationBudget || AloftPrice.zero;
          const roi = AloftPrice.schema
            .parse(opinionOfAfterRepairValue)
            .sub(AloftPrice.schema.parse(opinionOfMarketValue))
            .sub(AloftPrice.schema.parse(renovationBudget));
          if (roi.toDollarLike() > 0) {
            return;
          }
        }

        if (path.length >= 2) {
          const module = path[0] as string;
          const field = path[1] as string;

          let validationKey: ValidationKey | null = null;
          let targetModule: keyof OrganizedValidationErrors | null = null;

          switch (module) {
            case 'alerts':
              targetModule = 'Alerts';
              switch (field) {
                case 'alerts':
                  validationKey = ValidationKey.Alerts;
                  break;
                default:
                  break;
              }
              break;
            case 'orderDetails':
              targetModule = 'OrderDetails';
              switch (field) {
                case 'zoning':
                  validationKey = ValidationKey.Zoning;
                  break;
                case 'zoningCompliance':
                  validationKey = ValidationKey.ZoningCompliance;
                  if (code === 'invalid_enum_value' && issue.received === '') {
                    message = 'Please select a zoning compliance option.';
                  }
                  break;
                case 'parcelNumber':
                  validationKey = ValidationKey.ParcelNumber;
                  break;
                case 'legalDescription':
                  validationKey = ValidationKey.LegalDescription;
                  break;
                case 'hoa':
                  validationKey = ValidationKey.NeighborhoodName;
                  break;
                case 'effectiveDate':
                  validationKey = ValidationKey.EffectiveDate;
                  break;
                case 'intendedUsers':
                  validationKey = ValidationKey.IntendedUsers;
                  break;
                case 'totalRenovationBudget':
                  validationKey = ValidationKey.TotalRenovationBudget;
                  break;
                case 'intendedUse':
                  validationKey = ValidationKey.IntendedUse;
                  break;
                case 'locationType':
                  validationKey = ValidationKey.LocationType;
                  if (code === 'invalid_enum_value' && issue.received === '') {
                    message = 'Please select a location type option.';
                  }
                  break;
                case 'propertyInterest':
                  validationKey = ValidationKey.PropertyInterest;
                  if (code === 'invalid_enum_value' && issue.received === '') {
                    message = 'Please select a property interest option.';
                  }
                  break;
                case 'propertyInterestOther':
                  validationKey = ValidationKey.PropertyInterestOther;
                  break;
                default:
                  break;
              }
              break;
            case 'analysis':
              targetModule = 'Analysis';
              switch (field) {
                case 'opinionOfSiteValue':
                  if (!includeCostApproach) {
                    break;
                  }
                  validationKey = ValidationKey.OpinionOfSiteValue;
                  if (code === 'invalid_union')
                    message = 'Please enter a valid site value before continuing.';
                  break;
                default:
                  break;
              }
              break;
            case 'market':
              targetModule = 'Market';
              switch (field) {
                default:
                  break;
              }
              break;
            case 'commentary':
              targetModule = 'Commentary';
              switch (field) {
                case 'estimatedDemand':
                  validationKey = ValidationKey.EstimatedDemand;
                  break;
                case 'estimatedExposureTime':
                  validationKey = ValidationKey.EstimatedExposureTime;
                  break;
                case 'marketConditionCommentary':
                  validationKey = ValidationKey.MarketConditionCommentary;
                  break;
                case 'priorSalesCommentary':
                  validationKey = ValidationKey.PriorSalesCommentary;
                  break;
                default:
                  break;
              }
              break;
            case 'reconciliation': {
              const isArvRenovationBudget = field === 'totalRenovationBudget';
              targetModule = isArvRenovationBudget ? 'OrderDetails' : 'Reconciliation';
              if (
                !isArvRenovationBudget &&
                !shouldRequireReconciliationFields &&
                issue.message === 'Required'
              ) {
                break;
              }

              message = issue.message ?? 'Required';
              if (code === 'custom') {
                type = ValidationErrorType.INVALID;
              }
              switch (field) {
                case 'totalCostToCure':
                  validationKey = ValidationKey.TotalCostToCure;
                  break;
                case 'totalCostToCureCommentary':
                  validationKey = ValidationKey.TotalCostToCureCommentary;
                  break;
                case 'opinionOfMarketValue':
                  validationKey = ValidationKey.OpinionOfMarketValue;
                  break;
                case 'opinionOfAfterRepairValue':
                  validationKey = ValidationKey.OpinionOfAfterRepairValue;
                  break;
                case 'totalRenovationBudget':
                  validationKey = ValidationKey.TotalRenovationBudget;
                  break;
                case 'reconciliationCommentary':
                  validationKey = ValidationKey.ReconciliationCommentary;
                  break;
                case 'aivReconciliationCommentary':
                  validationKey = ValidationKey.AivReconciliationCommentary;
                  break;
                case 'arvReconciliationCommentary':
                  validationKey = ValidationKey.ArvReconciliationCommentary;
                  break;
                case 'highestAndBestUseCommentary':
                  validationKey = ValidationKey.HighestAndBestUseCommentary;
                  break;
                case 'revisionCommentary':
                  validationKey = ValidationKey.RevisionCommentary;
                  break;
                case 'alternativeComparablesCommentary':
                  validationKey = ValidationKey.AlternativeComparablesCommentary;
                  break;
                case 'feasibilityCommentary':
                  message = 'Feasibility Commentary is required when ROI is $0 or negative';
                  validationKey = ValidationKey.FeasibilityCommentary;
                  break;
                case 'aivDiscrepancyCommentary':
                  message = 'Required when As-Is value differs from prior Desktop';
                  validationKey = ValidationKey.AivDiscrepancyCommentary;
                  break;
                case 'arvDiscrepancyCommentary':
                  message = 'Required when After-Repair value differs from prior Desktop';
                  validationKey = ValidationKey.ArvDiscrepancyCommentary;
                  break;
                case 'arvBelowAivOverrideCommentary':
                  message = 'Required when overriding the After-Repair Value vs. As-Is Value check';
                  validationKey = ValidationKey.ArvBelowAivOverrideCommentary;
                  break;
                case 'valueTypes':
                  message = 'Please select a value type option';
                  validationKey = ValidationKey.ValueTypes;
                  break;
                default:
                  break;
              }
              break;
            }
            default:
              break;
          }

          if (validationKey && targetModule) {
            organizedErrors[targetModule][validationKey] = {
              type,
              message,
            };
          }
        }
      });
    }

    if (order.isEscalated) {
      organizedErrors.Global[ValidationKey.IsEscalated] = {
        type: ValidationErrorType.INVALID,
        message: 'Cannot certify this order while Appraisify escalates the issue to our client.',
      };
    }

    return organizedErrors;
  };

  const errors = processValidationErrors();

  return {
    errors,
  };
};
